/**
 * Utility functions for handling monetary amounts consistently across the application
 */

/**
 * Converts invoice total to display amount in pounds
 * Database stores amounts as DECIMAL(10,2) in POUNDS (e.g., 5.71 = £5.71)
 * @param {number|string} total - The invoice total from the database in pounds
 * @returns {number} - Amount in pounds for display
 */
export const getDisplayAmount = (total) => {
  // Handle null, undefined, or empty values
  if (total === null || total === undefined || total === '') {
    return 0;
  }
  
  // Convert to number - database returns DECIMAL as string "5.71" or number 5.71
  const amount = typeof total === 'string' ? parseFloat(total) : total;
  
  if (isNaN(amount) || !isFinite(amount)) {
    return 0;
  }
  
  // Return as-is - database stores in pounds
  return amount;
};

/**
 * Formats amount for display with currency symbol
 * @param {number|string} total - The invoice total from the database
 * @param {string} currency - Currency code (default: 'GBP')
 * @returns {string} - Formatted amount string
 */
export const formatAmount = (total, currency = 'GBP') => {
  const amount = getDisplayAmount(total);
  const symbol = currency === 'GBP' ? '£' : currency;
  return `${symbol}${amount.toFixed(2)}`;
};

/**
 * Formats amount for display with locale-specific formatting
 * @param {number|string} total - The invoice total from the database
 * @param {string} currency - Currency code (default: 'GBP')
 * @returns {string} - Locale-formatted amount string
 */
export const formatAmountLocale = (total, currency = 'GBP') => {
  const amount = getDisplayAmount(total);
  const symbol = currency === 'GBP' ? '£' : currency;
  
  // Ensure amount is a valid number
  if (isNaN(amount) || !isFinite(amount)) {
    return `${symbol}0.00`;
  }
  
  return `${symbol}${amount.toLocaleString('en-GB', { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  })}`;
};

/**
 * Converts pounds to cents for storage
 * @param {number} pounds - Amount in pounds
 * @returns {number} - Amount in cents
 */
export const poundsToCents = (pounds) => {
  return Math.round(pounds * 100);
};

/**
 * Converts cents to pounds for display
 * @param {number} cents - Amount in cents
 * @returns {number} - Amount in pounds
 */
export const centsToPounds = (cents) => {
  return cents / 100;
};
